import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, Divider, Tabs, Tab, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, CircularProgress,
  Checkbox, FormControlLabel, IconButton, Tooltip,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CategoryIcon from '@mui/icons-material/AccountTree';
import BackupIcon from '@mui/icons-material/Backup';
import RestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import { configAPI, backupAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// ---------------------------------------------------------------------------
// Chip-based editable list component for config items
// ---------------------------------------------------------------------------
function ChipConfigList({ title, description, items, onAdd, onUpdate, onDelete, fields }) {
  const [addForm, setAddForm] = useState(() => {
    const empty = {};
    fields.forEach((f) => { empty[f.field] = ''; });
    return empty;
  });
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleAdd = async () => {
    const anyEmpty = fields.some((f) => !addForm[f.field]?.trim());
    if (anyEmpty) return;
    await onAdd(addForm);
    const empty = {};
    fields.forEach((f) => { empty[f.field] = ''; });
    setAddForm(empty);
  };

  const openEdit = (item) => {
    const data = {};
    fields.forEach((f) => { data[f.field] = item[f.field] || ''; });
    setEditForm(data);
    setEditItem(item);
  };

  const handleEditSave = async () => {
    await onUpdate(editItem.id, editForm);
    setEditItem(null);
  };

  const confirmDelete = async () => {
    await onDelete(deleteTarget.id);
    setDeleteTarget(null);
  };

  const chipLabel = (item) => {
    if (item.label && item.value) return `${item.label} (${item.value})`;
    return item.label || item.name || item.value || '—';
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6">{title}</Typography>
      {description && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{description}</Typography>
      )}
      <Divider sx={{ mb: 2 }} />

      {items.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          No hay elementos configurados.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {items.map((item) => (
            <Chip
              key={item.id}
              label={chipLabel(item)}
              onClick={() => openEdit(item)}
              onDelete={() => setDeleteTarget(item)}
              color="default"
              variant="outlined"
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      )}

      {/* Inline add form */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
        {fields.map((f) => (
          <TextField
            key={f.field}
            size="small"
            label={f.label}
            value={addForm[f.field] || ''}
            onChange={(e) => setAddForm({ ...addForm, [f.field]: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            sx={{ minWidth: 180 }}
          />
        ))}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={fields.some((f) => !addForm[f.field]?.trim())}
        >
          Añadir
        </Button>
      </Box>

      {/* Edit dialog */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar elemento</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {fields.map((f) => (
              <Grid item xs={12} sm={f.fullWidth ? 12 : 6} key={f.field}>
                <TextField
                  fullWidth label={f.label} value={editForm[f.field] || ''}
                  onChange={(e) => setEditForm({ ...editForm, [f.field]: e.target.value })}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Cancelar</Button>
          <Button variant="contained" onClick={handleEditSave}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.dark' }}>
          <WarningAmberIcon color="warning" /> Eliminar elemento
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Vas a eliminar: <strong>{deleteTarget ? chipLabel(deleteTarget) : ''}</strong>
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            ⚠️ Esta acción puede causar pérdida de información en los proyectos que usen este elemento.
            Asegúrate de que no está en uso antes de continuar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} color="inherit">Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}


// ---------------------------------------------------------------------------
// Equipment categories component (chip style + delete confirmation)
// ---------------------------------------------------------------------------
const DEFAULT_CATEGORIES = [
  'Tanques', 'Bombas', 'Agitadores', 'Intercambiadores', 'Envasadoras',
  'Etiquetadoras', 'Paletizadoras', 'Transportadores', 'Filtros',
  'Dosificadores', 'Valvulas', 'Instrumentacion', 'Tuberias',
  'Cuadros Electricos', 'PLCs',
];

function EquipmentCategories() {
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [newCat, setNewCat] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleAdd = () => {
    const trimmed = newCat.trim();
    if (!trimmed || categories.includes(trimmed)) return;
    setCategories([...categories, trimmed]);
    setNewCat('');
  };

  const confirmDelete = () => {
    setCategories(categories.filter((c) => c !== deleteTarget));
    setDeleteTarget(null);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6">Categorias de Equipos</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Categorias disponibles para clasificar equipos en los proyectos.
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {categories.map((cat) => (
          <Chip key={cat} label={cat} onDelete={() => setDeleteTarget(cat)} variant="outlined" />
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small" label="Nueva categoria" value={newCat}
          onChange={(e) => setNewCat(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          sx={{ minWidth: 200 }}
        />
        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAdd} disabled={!newCat.trim()}>
          Añadir
        </Button>
      </Box>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.dark' }}>
          <WarningAmberIcon color="warning" /> Eliminar categoría
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Vas a eliminar la categoría: <strong>{deleteTarget}</strong>
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            ⚠️ Esta acción puede causar pérdida de información en los proyectos que usen esta categoría.
            Asegúrate de que no está en uso antes de continuar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} color="inherit">Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Menu structure for permission editing (no icons, no adminOnly items)
// ---------------------------------------------------------------------------
const AVAILABLE_MENUS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'clients', label: 'Clientes' },
  { id: 'suppliers', label: 'Proveedores' },
  { id: 'projects', label: 'Proyectos' },
  { id: 'chats', label: 'Chats' },
  { id: 'documents', label: 'Documentos' },
  {
    id: 'economic', label: 'Económico',
    children: [
      { id: 'budget', label: 'Presupuestos' },
      { id: 'invoices', label: 'Facturas' },
      { id: 'cashflow', label: 'Flujo de Caja' },
    ],
  },
  { id: 'planning', label: 'Planning' },
  { id: 'tasks', label: 'Tareas' },
  { id: 'reports', label: 'Informes' },
  { id: 'tools', label: 'Herramientas' },
];

function MenuPermissionSelector({ permissions, onChange }) {
  const toggleFlat = (id) => {
    onChange(permissions.includes(id)
      ? permissions.filter((x) => x !== id)
      : [...permissions, id]);
  };

  const toggleParent = (item) => {
    const allChildIds = item.children.map((c) => c.id);
    const allChecked = allChildIds.every((id) => permissions.includes(id));
    if (allChecked) {
      onChange(permissions.filter((x) => x !== item.id && !allChildIds.includes(x)));
    } else {
      onChange([...new Set([...permissions, item.id, ...allChildIds])]);
    }
  };

  const toggleChild = (parentItem, child) => {
    if (permissions.includes(child.id)) {
      const next = permissions.filter((x) => x !== child.id);
      const remainingChildren = parentItem.children.filter(
        (c) => c.id !== child.id && next.includes(c.id)
      );
      onChange(remainingChildren.length === 0
        ? next.filter((x) => x !== parentItem.id)
        : next);
    } else {
      onChange([...new Set([...permissions, child.id, parentItem.id])]);
    }
  };

  return (
    <Box sx={{ pl: 1 }}>
      {AVAILABLE_MENUS.map((item) => {
        if (!item.children) {
          return (
            <Box key={item.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={permissions.includes(item.id)}
                    onChange={() => toggleFlat(item.id)}
                    size="small"
                  />
                }
                label={item.label}
              />
            </Box>
          );
        }
        const allChecked = item.children.every((c) => permissions.includes(c.id));
        const someChecked = item.children.some((c) => permissions.includes(c.id));
        return (
          <Box key={item.id}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allChecked}
                  indeterminate={!allChecked && someChecked}
                  onChange={() => toggleParent(item)}
                  size="small"
                />
              }
              label={<Typography variant="body2" fontWeight="bold">{item.label}</Typography>}
            />
            <Box sx={{ pl: 3 }}>
              {item.children.map((child) => (
                <Box key={child.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={permissions.includes(child.id)}
                        onChange={() => toggleChild(item, child)}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">{child.label}</Typography>}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function CategoryManager() {
  const { reloadRolePermissions } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [roleError, setRoleError] = useState(null);

  // Edit existing role
  const [editItem, setEditItem] = useState(null);
  const [editPerms, setEditPerms] = useState([]);
  const [editLabel, setEditLabel] = useState('');

  // Create new role
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ role: '', label: '', permissions: [] });
  const [createError, setCreateError] = useState('');

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => { loadRoles(); }, []);

  const loadRoles = async () => {
    try {
      const res = await configAPI.listRolePermissions();
      setRoles(res.data);
    } catch (_) {
      setRoleError('Error al cargar los permisos de roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  const openEdit = (roleItem) => {
    setEditPerms([...roleItem.permissions]);
    setEditLabel(roleItem.label);
    setEditItem(roleItem);
  };

  const handleSave = async () => {
    try {
      await configAPI.updateRolePermissions(
        editItem.role,
        editPerms,
        editItem.is_system ? undefined : editLabel,
      );
      await loadRoles();
      await reloadRolePermissions();
      setEditItem(null);
    } catch (_) {
      setRoleError('Error al guardar los permisos');
    }
  };

  const handleCreate = async () => {
    setCreateError('');
    if (!createForm.role.trim() || !createForm.label.trim()) {
      setCreateError('La clave y el nombre son obligatorios.');
      return;
    }
    try {
      await configAPI.createRolePermissions(createForm);
      await loadRoles();
      await reloadRolePermissions();
      setCreateOpen(false);
      setCreateForm({ role: '', label: '', permissions: [] });
    } catch (err) {
      setCreateError(err?.response?.data?.detail || 'Error al crear el rol');
    }
  };

  const handleDelete = async () => {
    try {
      await configAPI.deleteRolePermissions(deleteTarget.role);
      await loadRoles();
      await reloadRolePermissions();
      setDeleteTarget(null);
    } catch (err) {
      setRoleError(err?.response?.data?.detail || 'Error al eliminar el rol');
      setDeleteTarget(null);
    }
  };

  const permissionSummary = (perms) => {
    const labels = [];
    AVAILABLE_MENUS.forEach((item) => {
      if (item.children) {
        const allowed = item.children.filter((c) => perms.includes(c.id));
        if (allowed.length > 0) labels.push(`${item.label}: ${allowed.map((c) => c.label).join(', ')}`);
      } else if (perms.includes(item.id)) {
        labels.push(item.label);
      }
    });
    return labels;
  };

  const ROLE_COLORS = { admin: 'error', manager: 'warning', engineer: 'primary', viewer: 'default' };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h6">Gestión de Roles</Typography>
          <Typography variant="body2" color="textSecondary">
            Configura qué menús puede ver cada rol. Puedes añadir roles personalizados además de los del sistema.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setCreateForm({ role: '', label: '', permissions: [] }); setCreateError(''); setCreateOpen(true); }}>
          Nuevo Rol
        </Button>
      </Box>

      {roleError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRoleError(null)}>{roleError}</Alert>}

      {loadingRoles ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={2}>
          {roles.map((roleItem) => {
            const isAdminRole = roleItem.role === 'admin';
            const summary = isAdminRole ? ['Acceso completo (no editable)'] : permissionSummary(roleItem.permissions);
            return (
              <Grid item xs={12} sm={6} key={roleItem.role}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={roleItem.label} color={ROLE_COLORS[roleItem.role] || 'secondary'} size="small" />
                      <Typography variant="caption" color="textSecondary">({roleItem.role})</Typography>
                      {roleItem.is_system && <Chip label="sistema" size="small" variant="outlined" color="default" />}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {!isAdminRole && (
                        <IconButton size="small" onClick={() => openEdit(roleItem)}><EditIcon fontSize="small" /></IconButton>
                      )}
                      {!roleItem.is_system && (
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(roleItem)}>
                          <WarningAmberIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                  {summary.length === 0 ? (
                    <Typography variant="caption" color="error">Sin accesos asignados</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {summary.map((label, i) => (
                        <Chip key={i} label={label} size="small" variant="outlined"
                          color={isAdminRole ? 'success' : 'primary'} />
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Edit permissions dialog */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon color="primary" />
          {editItem && `Editar rol: ${editItem.label}`}
        </DialogTitle>
        <DialogContent>
          {editItem && !editItem.is_system && (
            <TextField
              fullWidth label="Nombre visible" value={editLabel} size="small"
              onChange={(e) => setEditLabel(e.target.value)}
              sx={{ mt: 1, mb: 2 }}
            />
          )}
          <Typography variant="subtitle2" gutterBottom>Menús visibles</Typography>
          <Divider sx={{ mb: 1 }} />
          <MenuPermissionSelector permissions={editPerms} onChange={setEditPerms} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Create role dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon color="primary" /> Nuevo Rol
        </DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <TextField
            fullWidth label="Nombre visible" value={createForm.label} size="small"
            onChange={(e) => setCreateForm({ ...createForm, label: e.target.value })}
            placeholder="Ej: Ingeniero Junior"
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth label="Clave interna (sin espacios, minúsculas)" value={createForm.role} size="small"
            onChange={(e) => setCreateForm({ ...createForm, role: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
            placeholder="Ej: junior_engineer"
            helperText="Se usa como identificador técnico. No se puede cambiar después."
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" gutterBottom>Menús visibles</Typography>
          <Divider sx={{ mb: 1 }} />
          <MenuPermissionSelector
            permissions={createForm.permissions}
            onChange={(perms) => setCreateForm({ ...createForm, permissions: perms })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate}>Crear Rol</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.dark' }}>
          <WarningAmberIcon color="warning" /> Eliminar rol
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Vas a eliminar el rol: <strong>{deleteTarget?.label}</strong> ({deleteTarget?.role})
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            ⚠️ Los usuarios con este rol perderán acceso al menú configurado. Asegúrate de reasignar sus roles antes de continuar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} color="inherit">Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Recursive folder tree editor — supports unlimited depth
// Internally stores paths as flat string array: ["02_OFERTAS", "02_OFERTAS/CLIENTE"]
// ---------------------------------------------------------------------------
function FolderTreeEditor({ paths, onChange }) {
  const [addingUnder, setAddingUnder] = useState(null); // path string or '' for root
  const [newName, setNewName] = useState('');

  // Build map: parentPath -> [childPath]
  const buildMap = (allPaths) => {
    const map = {};
    for (const p of allPaths) {
      const sep = p.lastIndexOf('/');
      const parent = sep === -1 ? '' : p.substring(0, sep);
      if (!map[parent]) map[parent] = [];
      map[parent].push(p);
    }
    return map;
  };

  const childrenMap = buildMap(paths);

  const addFolder = (parentPath) => {
    const trimmed = newName.trim().replace(/\//g, '_'); // no slashes in name
    if (!trimmed) return;
    const fullPath = parentPath ? `${parentPath}/${trimmed}` : trimmed;
    if (!paths.includes(fullPath)) {
      onChange([...paths, fullPath]);
    }
    setAddingUnder(null);
    setNewName('');
  };

  const removeFolder = (path) => {
    onChange(paths.filter(p => p !== path && !p.startsWith(path + '/')));
  };

  const moveSibling = (path, dir) => {
    const sep = path.lastIndexOf('/');
    const parent = sep === -1 ? '' : path.substring(0, sep);
    const siblings = childrenMap[parent] || [];
    const idx = siblings.indexOf(path);
    const to = idx + dir;
    if (to < 0 || to >= siblings.length) return;
    // swap the two paths in the flat list, preserving child order
    const newPaths = [...paths];
    const ia = newPaths.indexOf(siblings[idx]);
    const ib = newPaths.indexOf(siblings[to]);
    [newPaths[ia], newPaths[ib]] = [newPaths[ib], newPaths[ia]];
    onChange(newPaths);
  };

  const renderNode = (path, depth) => {
    const label = path.split('/').pop();
    const children = childrenMap[path] || [];
    const sep = path.lastIndexOf('/');
    const parent = sep === -1 ? '' : path.substring(0, sep);
    const siblings = childrenMap[parent] || [];
    const idx = siblings.indexOf(path);
    const isAdding = addingUnder === path;

    return (
      <Box key={path}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, pl: depth * 3 }}>
          <Typography variant="body2" sx={{
            fontFamily: 'monospace', flex: 1,
            p: '4px 10px',
            bgcolor: depth === 0 ? '#e3edf9' : depth === 1 ? '#f0f4f8' : '#f8f9fa',
            borderRadius: 1,
            border: `1px solid ${depth === 0 ? '#b0c4de' : '#dde3ea'}`,
            fontWeight: depth === 0 ? 600 : 400,
          }}>
            {'\u00a0\u00a0'.repeat(depth)}📁 {label}
          </Typography>
          <Tooltip title="Añadir subcarpeta aquí">
            <IconButton size="small" color="primary"
              onClick={() => { setAddingUnder(path); setNewName(''); }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => moveSibling(path, -1)} disabled={idx === 0} title="Subir">▲</IconButton>
          <IconButton size="small" onClick={() => moveSibling(path, 1)} disabled={idx >= siblings.length - 1} title="Bajar">▼</IconButton>
          <IconButton size="small" color="error" onClick={() => removeFolder(path)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>

        {isAdding && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1, pl: (depth + 1) * 3 }}>
            <TextField size="small" autoFocus
              label={`Subcarpeta dentro de ${label}`}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addFolder(path);
                if (e.key === 'Escape') setAddingUnder(null);
              }}
              placeholder="Ej: CLIENTE"
              sx={{ flex: 1 }}
            />
            <Button size="small" variant="contained" onClick={() => addFolder(path)}>OK</Button>
            <Button size="small" onClick={() => setAddingUnder(null)}>Cancelar</Button>
          </Box>
        )}

        {children.map(child => renderNode(child, depth + 1))}
      </Box>
    );
  };

  const roots = childrenMap[''] || [];

  return (
    <Box>
      {roots.map(p => renderNode(p, 0))}

      {addingUnder === '' ? (
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField size="small" autoFocus
            label="Nueva carpeta raíz"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') addFolder('');
              if (e.key === 'Escape') setAddingUnder(null);
            }}
            placeholder="Ej: 05_CALIDAD"
            sx={{ flex: 1 }}
          />
          <Button size="small" variant="contained" onClick={() => addFolder('')}>OK</Button>
          <Button size="small" onClick={() => setAddingUnder(null)}>Cancelar</Button>
        </Box>
      ) : (
        <Button variant="outlined" startIcon={<AddIcon />} sx={{ mt: 1 }}
          onClick={() => { setAddingUnder(''); setNewName(''); }}>
          Añadir carpeta raíz
        </Button>
      )}
    </Box>
  );
}


// ---------------------------------------------------------------------------
// Company settings component (data + logo)
// ---------------------------------------------------------------------------
const COMPANY_FIELDS = [
  { key: 'company_name', label: 'Nombre de la empresa', xs: 12, sm: 6 },
  { key: 'company_cif', label: 'CIF / NIF', xs: 12, sm: 6 },
  { key: 'company_address', label: 'Direccion', xs: 12 },
  { key: 'company_city', label: 'Ciudad', xs: 12, sm: 4 },
  { key: 'company_postal_code', label: 'Codigo Postal', xs: 12, sm: 4 },
  { key: 'company_country', label: 'Pais', xs: 12, sm: 4 },
  { key: 'company_phone', label: 'Telefono', xs: 12, sm: 6 },
  { key: 'company_email', label: 'Email', xs: 12, sm: 6 },
  { key: 'company_web', label: 'Web', xs: 12 },
];

const MAX_LOGO_SIZE = 1024 * 1024; // 1 MB

function CompanySettings({ onSuccess, onError }) {
  const { reloadCompany } = useAuth();
  const [values, setValues] = useState({});
  const [logo, setLogo] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const resp = await configAPI.getSettings();
      const s = resp.data || {};
      const vals = {};
      COMPANY_FIELDS.forEach((f) => { vals[f.key] = s[f.key] || ''; });
      setValues(vals);
      setLogo(s.company_logo || '');
    } catch (_) {
      if (onError) onError('Error al cargar datos de empresa');
    }
  };

  const handleChange = (key) => (e) => setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = async () => {
    try {
      setSaving(true);
      await Promise.all(
        COMPANY_FIELDS.map((f) => configAPI.updateSetting(f.key, values[f.key] || ''))
      );
      if (onSuccess) onSuccess('Datos de empresa guardados');
      if (reloadCompany) await reloadCompany();
    } catch (_) {
      if (onError) onError('Error al guardar datos de empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      if (onError) onError('El archivo debe ser una imagen');
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      if (onError) onError('La imagen no puede superar 1 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      try {
        setUploading(true);
        await configAPI.updateSetting('company_logo', dataUrl);
        setLogo(dataUrl);
        if (onSuccess) onSuccess('Logo actualizado');
        if (reloadCompany) await reloadCompany();
      } catch (_) {
        if (onError) onError('Error al guardar el logo');
      } finally {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = '';
      }
    };
    reader.onerror = () => { if (onError) onError('No se pudo leer el archivo'); };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = async () => {
    try {
      setUploading(true);
      await configAPI.updateSetting('company_logo', '');
      setLogo('');
      if (onSuccess) onSuccess('Logo eliminado');
      if (reloadCompany) await reloadCompany();
    } catch (_) {
      if (onError) onError('Error al eliminar el logo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Datos de la Empresa</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Informacion corporativa y logo de la empresa que se mostrara en la aplicacion.
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" gutterBottom>Logotipo</Typography>
          <Paper
            variant="outlined"
            sx={{
              width: '100%', minHeight: 160, display: 'flex', alignItems: 'center',
              justifyContent: 'center', bgcolor: '#fafafa', p: 2, mb: 1,
            }}
          >
            {logo ? (
              <Box component="img" src={logo} alt="Logo de la empresa"
                sx={{ maxWidth: '100%', maxHeight: 180, objectFit: 'contain' }} />
            ) : (
              <Typography variant="caption" color="textSecondary">
                Sin logo. Sube una imagen (PNG, JPG, SVG) de max. 1 MB.
              </Typography>
            )}
          </Paper>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleLogoSelected}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {logo ? 'Cambiar logo' : 'Subir logo'}
            </Button>
            {logo && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={uploading}
                onClick={handleRemoveLogo}
              >
                Quitar
              </Button>
            )}
            {uploading && <CircularProgress size={20} />}
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            {COMPANY_FIELDS.map((f) => (
              <Grid item xs={f.xs} sm={f.sm || f.xs} key={f.key}>
                <TextField
                  fullWidth
                  label={f.label}
                  value={values[f.key] || ''}
                  onChange={handleChange(f.key)}
                />
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" disabled={saving} onClick={handleSave}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}


// ---------------------------------------------------------------------------
// Document path settings component
// ---------------------------------------------------------------------------
function DocumentSettings({ onSuccess, onError }) {
  const [basePath, setBasePath] = useState('/app/documents');
  const [template, setTemplate] = useState('{continent}/{year}/{client}/{project_code} {project_name}');
  const [subfolders, setSubfolders] = useState([]);
  const [offerTemplate, setOfferTemplate] = useState('{client}/{offer_code} {project_name}');
  const [supplierOffersPath, setSupplierOffersPath] = useState('INGENIERIA/OFERTA PROVEEDORES');
  const [offerSubfolders, setOfferSubfolders] = useState([]);
  const [backend, setBackend] = useState('local');
  const [sharedDriveId, setSharedDriveId] = useState('');
  const [driveRootFolderId, setDriveRootFolderId] = useState('');
  const [projectsRootId, setProjectsRootId] = useState('');
  const [offersRootId, setOffersRootId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const resp = await configAPI.getSettings();
      const s = resp.data;
      if (s.documents_base_path) setBasePath(s.documents_base_path);
      if (s.project_path_template) setTemplate(s.project_path_template);
      if (s.offer_path_template) setOfferTemplate(s.offer_path_template);
      if (s.supplier_offers_folder_path) setSupplierOffersPath(s.supplier_offers_folder_path);
      if (s.documents_backend) setBackend(s.documents_backend);
      setSharedDriveId(s.drive_shared_drive_id || '');
      setDriveRootFolderId(s.drive_root_folder_id || '');
      setProjectsRootId(s.drive_projects_root_folder_id || '');
      setOffersRootId(s.drive_offers_root_folder_id || '');
      try { setSubfolders(JSON.parse(s.project_subfolders || '[]')); } catch { setSubfolders([]); }
      try { setOfferSubfolders(JSON.parse(s.offer_subfolders || '[]')); } catch { setOfferSubfolders([]); }
    } catch (err) { console.error('Error loading doc settings:', err); }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await Promise.all([
        configAPI.updateSetting('documents_base_path', basePath),
        configAPI.updateSetting('project_path_template', template),
        configAPI.updateSetting('project_subfolders', JSON.stringify(subfolders)),
        configAPI.updateSetting('offer_path_template', offerTemplate),
        configAPI.updateSetting('supplier_offers_folder_path', supplierOffersPath.trim()),
        configAPI.updateSetting('offer_subfolders', JSON.stringify(offerSubfolders)),
        configAPI.updateSetting('documents_backend', backend),
        configAPI.updateSetting('drive_shared_drive_id', sharedDriveId.trim()),
        configAPI.updateSetting('drive_root_folder_id', driveRootFolderId.trim()),
        configAPI.updateSetting('drive_projects_root_folder_id', projectsRootId.trim()),
        configAPI.updateSetting('drive_offers_root_folder_id', offersRootId.trim()),
      ]);
      if (onSuccess) onSuccess('Configuración de documentos guardada');
    } catch (err) { if (onError) onError('Error al guardar la configuración'); }
    finally { setSaving(false); }
  };

  const fillExample = (tpl) => tpl
    .replace('{continent}', 'EUROPA')
    .replace('{year}', new Date().getFullYear())
    .replace('{client}', 'CLIENTE')
    .replace('{project_code}', '26001')
    .replace('{offer_code}', 'OF-2026-001')
    .replace('{project_name}', 'Nombre del proyecto');

  const projectRootLabel = backend === 'drive' ? '[Raíz de PROYECTOS]' : basePath;
  const examplePath = `${projectRootLabel}/${fillExample(template)}`;
  const exampleOfferPath = `[Raíz de OFERTAS]/${fillExample(offerTemplate)}`;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Rutas de Documentos</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Define dónde se almacenan los documentos y qué carpetas se crean automáticamente al crear un proyecto.
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            label="Dónde se almacenan los documentos"
            value={backend}
            onChange={(e) => setBackend(e.target.value)}
            helperText="Cambiar el almacén no mueve ficheros: sólo cambia lo que muestra la pestaña Documentos."
          >
            <MenuItem value="local">Servidor (sistema de ficheros)</MenuItem>
            <MenuItem value="drive">Google Drive (unidad compartida)</MenuItem>
          </TextField>
        </Grid>

        {backend === 'drive' ? (
          <>
            <Grid item xs={12}>
              <Alert severity="info">
                Cada usuario debe conectar su cuenta de Google desde <strong>Mi Cuenta</strong>.
                La aplicación accede a Drive en nombre de cada persona, respetando los permisos
                de la unidad compartida.
              </Alert>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ID de la unidad compartida"
                value={sharedDriveId}
                onChange={(e) => setSharedDriveId(e.target.value)}
                helperText="Empieza por 0A… Se ve en la URL al abrir la unidad: drive.google.com/drive/folders/ID"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Carpeta raíz del explorador (opcional)"
                value={driveRootFolderId}
                onChange={(e) => setDriveRootFolderId(e.target.value)}
                helperText="Dónde empieza a navegar la pestaña Documentos. Vacío = raíz de la unidad."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Carpeta raíz de proyectos"
                value={projectsRootId}
                onChange={(e) => setProjectsRootId(e.target.value)}
                helperText="Rama PROYECTOS: dentro cuelgan los continentes. Ej: CNPK/PROYECTOS/PROYECTOS"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Carpeta raíz de ofertas"
                value={offersRootId}
                onChange={(e) => setOffersRootId(e.target.value)}
                helperText="Rama OFERTAS. Ej: CNPK/DEPT.COMERCIAL/OFERTAS/CNPK"
              />
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Ruta base en el servidor"
              value={basePath}
              onChange={(e) => setBasePath(e.target.value)}
              helperText="Ruta absoluta en el servidor donde se guardan los documentos. Ej: /mnt/nas/PROYECTOS"
            />
          </Grid>
        )}

        {backend === 'drive' && (
          <Grid item xs={12}>
            <Divider sx={{ mt: 1 }}>
              <Chip label="Rama OFERTAS" size="small" />
            </Divider>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              La carpeta de la oferta se crea automáticamente al dar de alta una petición.
            </Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Carpeta de ofertas de proveedor"
            value={supplierOffersPath}
            onChange={(e) => setSupplierOffersPath(e.target.value)}
            helperText={
              <span>
                Dentro de la carpeta de cada proyecto. Al arrastrar una oferta en
                Ing. Detalle se archiva aquí, en una subcarpeta con el nombre del
                proveedor, creando lo que falte.
              </span>
            }
          />
        </Grid>

        {backend === 'drive' && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plantilla de ruta de la oferta"
                value={offerTemplate}
                onChange={(e) => setOfferTemplate(e.target.value)}
                helperText={
                  <span>
                    Cuelga de la raíz de ofertas. Variables: <strong>{'{client}'}</strong>,{' '}
                    <strong>{'{offer_code}'}</strong> (n° oferta), <strong>{'{project_name}'}</strong>,{' '}
                    <strong>{'{year}'}</strong>, <strong>{'{continent}'}</strong>
                  </span>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="caption" color="textSecondary">Ejemplo de ruta de oferta:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 0.5 }}>
                  {exampleOfferPath}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Subcarpetas de la oferta</Typography>
              <FolderTreeEditor paths={offerSubfolders} onChange={setOfferSubfolders} />
            </Grid>
          </>
        )}

        {backend === 'drive' && (
          <Grid item xs={12}>
            <Divider sx={{ mt: 1 }}>
              <Chip label="Rama PROYECTOS" size="small" />
            </Divider>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Al pasar de Ing. Detalle a Ejecución, la carpeta de la oferta se <strong>copia</strong> aquí
              renombrada con el nº de proyecto. La oferta original se conserva.
            </Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Plantilla de ruta del proyecto"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            helperText={
              <span>
                Variables: <strong>{'{continent}'}</strong> (continente), <strong>{'{year}'}</strong> (año),{' '}
                <strong>{'{client}'}</strong> (cliente), <strong>{'{project_code}'}</strong> (n° proyecto),{' '}
                <strong>{'{offer_code}'}</strong> (n° oferta), <strong>{'{project_name}'}</strong> (nombre)
              </span>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="caption" color="textSecondary">Ejemplo de ruta resultante:</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mt: 0.5 }}>
              {examplePath}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Estructura de subcarpetas del proyecto</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Carpetas que se crearán automáticamente al crear un proyecto. Usa el botón{' '}
            <strong>📁+</strong> de cada carpeta para añadir subcarpetas anidadas.
          </Typography>
          <FolderTreeEditor paths={subfolders} onChange={setSubfolders} />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </Button>
      </Box>
    </Paper>
  );
}


// ---------------------------------------------------------------------------
// DatabaseBackupPanel  —  admin only
// ---------------------------------------------------------------------------
function DatabaseBackupPanel() {
  const { user } = useAuth();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreMsg, setRestoreMsg] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) loadBackups();
  // eslint-disable-next-line
  }, [isAdmin]);

  const loadBackups = async () => {
    setLoading(true);
    try {
      const res = await backupAPI.list();
      setBackups(res.data);
    } catch (err) {
      setError('Error al cargar la lista de backups.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true); setError(null);
    try {
      await backupAPI.create();
      setSuccess('Backup creado correctamente.');
      await loadBackups();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear el backup.');
    } finally {
      setCreating(false);
      setTimeout(() => setSuccess(null), 4000);
    }
  };

  const handleDelete = async () => {
    try {
      await backupAPI.delete(deleteTarget);
      setDeleteTarget(null);
      setSuccess('Backup eliminado.');
      await loadBackups();
    } catch (err) {
      setError('Error al eliminar el backup.');
    } finally {
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    setRestoreMsg('Iniciando restauración...');
    try {
      // This returns immediately with a job_id (no HTTP timeout risk)
      const res = await backupAPI.restore(restoreTarget);
      const jobId = res.data.job_id;
      setRestoreTarget(null); // close dialog right away

      // Poll every 2 s until done or error
      const poll = setInterval(async () => {
        try {
          const status = await backupAPI.restoreStatus(jobId);
          setRestoreMsg(status.data.message);
          if (status.data.status === 'done') {
            clearInterval(poll);
            setRestoring(false);
            setRestoreMsg('');
            setSuccess('Base de datos restaurada correctamente. Recarga la página para ver los cambios.');
            setTimeout(() => setSuccess(null), 10000);
          } else if (status.data.status === 'error') {
            clearInterval(poll);
            setRestoring(false);
            setRestoreMsg('');
            setError(status.data.message);
          }
        } catch (pollErr) {
          clearInterval(poll);
          setRestoring(false);
          setRestoreMsg('');
          setError('Error al consultar el estado de la restauración.');
        }
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar la restauración.');
      setRestoreTarget(null);
      setRestoring(false);
      setRestoreMsg('');
    }
  };

  const handleDownload = async (filename) => {
    try {
      const res = await backupAPI.download(filename);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error al descargar el backup.');
    }
  };

  if (!isAdmin) {
    return <Paper sx={{ p: 3 }}><Alert severity="warning">Acceso restringido a administradores.</Alert></Paper>;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <StorageIcon color="primary" />
        <Typography variant="h6">Gestión de Backups</Typography>
      </Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Crea, descarga, restaura o elimina backups de la base de datos. Solo accesible para administradores.
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}
      {restoring && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={18} />}>
          {restoreMsg || 'Restaurando...'}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={creating ? <CircularProgress size={18} color="inherit" /> : <BackupIcon />}
          onClick={handleCreate}
          disabled={creating || loading}
        >
          {creating ? 'Creando backup...' : 'Crear backup ahora'}
        </Button>
        <Tooltip title="Actualizar lista">
          <span>
            <IconButton onClick={loadBackups} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {backups.length === 0 && !loading ? (
        <Typography color="textSecondary">No hay backups disponibles.</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Archivo</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Fecha creacion</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600 }}>Tamaño</th>
                <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.filename} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: '0.85rem' }}>{b.filename}</td>
                  <td style={{ padding: '8px 12px' }}>{b.created_at}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{b.size_human}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <Tooltip title="Descargar backup">
                      <IconButton size="small" color="primary" onClick={() => handleDownload(b.filename)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Restaurar base de datos">
                      <IconButton size="small" color="warning" onClick={() => setRestoreTarget(b.filename)}>
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar backup">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(b.filename)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}

      {/* Confirm delete */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar backup</DialogTitle>
        <DialogContent>
          <Typography>¿Seguro que deseas eliminar <strong>{deleteTarget}</strong>? Esta accion no se puede deshacer.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm restore */}
      <Dialog open={!!restoreTarget} onClose={() => !restoring && setRestoreTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" /> Restaurar base de datos
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta operacion <strong>sobreescribira todos los datos actuales</strong> con los del backup seleccionado. Esta accion no se puede deshacer.
          </Alert>
          <Typography>Backup seleccionado: <strong>{restoreTarget}</strong></Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreTarget(null)} disabled={restoring}>Cancelar</Button>
          <Button
            color="error" variant="contained"
            onClick={handleRestore}
            disabled={restoring}
            startIcon={restoring ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
          >
            {restoring ? 'Restaurando...' : 'Restaurar ahora'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main Settings component
// ---------------------------------------------------------------------------
export default function Settings() {
  const [activeTab, setActiveTab] = useState(0);
  const [projectTypes, setProjectTypes] = useState([]);
  const [containerTypes, setContainerTypes] = useState([]);
  const [costPartidas, setCostPartidas] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [selectedProjectType, setSelectedProjectType] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [ptResp, ctResp, discResp] = await Promise.all([
        configAPI.listProjectTypes(),
        configAPI.listContainerTypes(),
        configAPI.listDisciplines(),
      ]);
      setProjectTypes(ptResp.data);
      setContainerTypes(ctResp.data);
      setDisciplines(discResp.data);
      // Load partidas for first project type if available
      if (ptResp.data.length > 0) {
        const firstType = ptResp.data[0].value;
        setSelectedProjectType(firstType);
        const cpResp = await configAPI.listCostPartidas(firstType);
        setCostPartidas(cpResp.data);
      }
    } catch (err) {
      console.error('Error loading config:', err);
      setError('Error al cargar la configuracion.');
    } finally {
      setLoading(false);
    }
  };

  const loadPartidas = async (projectType) => {
    try {
      const resp = await configAPI.listCostPartidas(projectType);
      setCostPartidas(resp.data);
    } catch (err) {
      console.error('Error loading partidas:', err);
    }
  };

  const handleProjectTypeChange = (value) => {
    setSelectedProjectType(value);
    loadPartidas(value);
  };

  // Project types handlers
  const ptHandlers = {
    onAdd: async (data) => {
      try {
        await configAPI.createProjectType(data);
        const resp = await configAPI.listProjectTypes();
        setProjectTypes(resp.data);
        setSuccess('Tipo de proyecto creado');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) { setError('Error al crear tipo de proyecto'); }
    },
    onUpdate: async (id, data) => {
      try {
        await configAPI.updateProjectType(id, data);
        const resp = await configAPI.listProjectTypes();
        setProjectTypes(resp.data);
        setSuccess('Tipo de proyecto actualizado');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) { setError('Error al actualizar'); }
    },
    onDelete: async (id) => {
      try {
        await configAPI.deleteProjectType(id);
        const resp = await configAPI.listProjectTypes();
        setProjectTypes(resp.data);
      } catch (err) { setError('Error al eliminar'); }
    },
  };

  // Container types handlers
  const ctHandlers = {
    onAdd: async (data) => {
      try {
        await configAPI.createContainerType(data);
        const resp = await configAPI.listContainerTypes();
        setContainerTypes(resp.data);
        setSuccess('Tipo de envase creado');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) { setError('Error al crear tipo de envase'); }
    },
    onUpdate: async (id, data) => {
      try {
        await configAPI.updateContainerType(id, data);
        const resp = await configAPI.listContainerTypes();
        setContainerTypes(resp.data);
        setSuccess('Tipo de envase actualizado');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) { setError('Error al actualizar'); }
    },
    onDelete: async (id) => {
      try {
        await configAPI.deleteContainerType(id);
        const resp = await configAPI.listContainerTypes();
        setContainerTypes(resp.data);
      } catch (err) { setError('Error al eliminar'); }
    },
  };

  // Discipline handlers
  const discHandlers = {
    onAdd: async (data) => {
      try {
        await configAPI.createDiscipline(data);
        const resp = await configAPI.listDisciplines();
        setDisciplines(resp.data);
        setSuccess('Disciplina creada');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) { setError('Error al crear disciplina'); }
    },
    onUpdate: async (id, data) => {
      try {
        await configAPI.updateDiscipline(id, data);
        const resp = await configAPI.listDisciplines();
        setDisciplines(resp.data);
        setSuccess('Disciplina actualizada');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) { setError('Error al actualizar'); }
    },
    onDelete: async (id) => {
      try {
        await configAPI.deleteDiscipline(id);
        const resp = await configAPI.listDisciplines();
        setDisciplines(resp.data);
      } catch (err) { setError('Error al eliminar'); }
    },
  };

  // Cost partidas handlers
  const cpHandlers = {
    onAdd: async (data) => {
      try {
        await configAPI.createCostPartida({ ...data, project_type: selectedProjectType });
        await loadPartidas(selectedProjectType);
        setSuccess('Partida creada');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) { setError('Error al crear partida'); }
    },
    onUpdate: async (id, data) => {
      try {
        await configAPI.updateCostPartida(id, { ...data, project_type: selectedProjectType });
        await loadPartidas(selectedProjectType);
        setSuccess('Partida actualizada');
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) { setError('Error al actualizar'); }
    },
    onDelete: async (id) => {
      try {
        await configAPI.deleteCostPartida(id);
        await loadPartidas(selectedProjectType);
      } catch (err) { setError('Error al eliminar'); }
    },
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AdminPanelSettingsIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" fontWeight="bold">Configuracion del Sistema</Typography>
        <Chip label="Solo Administradores" color="error" size="small" variant="outlined" />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Tipos de Proyecto" />
          <Tab label="Tipos de Envase" />
          <Tab label="Partidas de Coste" />
          <Tab label="Disciplinas" />
          <Tab label="Categorias de Equipos" />
          <Tab label="Gestión de Roles" />
          <Tab label="Empresa" />
          <Tab label="Rutas y Documentos" />
          <Tab label="Base de Datos" icon={<StorageIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Project Types */}
      <TabPanel value={activeTab} index={0}>
        <ChipConfigList
          title="Tipos de Proyecto"
          description="Define los tipos de proyecto disponibles al crear un nuevo proyecto."
          items={projectTypes}
          fields={[
            { field: 'value', label: 'Valor interno (ej: detergent)' },
            { field: 'label', label: 'Nombre visible (ej: Detergentes)' },
          ]}
          {...ptHandlers}
        />
      </TabPanel>

      {/* Tab 1: Container Types */}
      <TabPanel value={activeTab} index={1}>
        <ChipConfigList
          title="Tipos de Envase"
          description="Define los tipos de envase disponibles en los formatos de envasado."
          items={containerTypes}
          fields={[
            { field: 'value', label: 'Valor interno (ej: botella)' },
            { field: 'label', label: 'Nombre visible (ej: Botella)' },
          ]}
          {...ctHandlers}
        />
      </TabPanel>

      {/* Tab 2: Cost Partidas */}
      <TabPanel value={activeTab} index={2}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Partidas de Coste por Tipo de Proyecto</Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Cada tipo de proyecto puede tener su propia lista de partidas de coste.
            Selecciona el tipo de proyecto para gestionar sus partidas.
          </Typography>
          <TextField
            select fullWidth label="Tipo de Proyecto" value={selectedProjectType}
            onChange={(e) => handleProjectTypeChange(e.target.value)}
            sx={{ mt: 2, mb: 2 }}
          >
            {projectTypes.map((pt) => (
              <MenuItem key={pt.value} value={pt.value}>{pt.label}</MenuItem>
            ))}
          </TextField>

          {selectedProjectType && (
            <ChipConfigList
              title={`Partidas para: ${projectTypes.find((p) => p.value === selectedProjectType)?.label || selectedProjectType}`}
              description="Estas partidas se mostraran en la estimacion de costes de la ingenieria basica."
              items={costPartidas}
              fields={[
                { field: 'name', label: 'Nombre de la partida', fullWidth: true },
              ]}
              {...cpHandlers}
            />
          )}
        </Paper>
      </TabPanel>

      {/* Tab 3: Disciplines */}
      <TabPanel value={activeTab} index={3}>
        <ChipConfigList
          title="Disciplinas de Ingeniería"
          description="Disciplinas disponibles para clasificar tareas en Ingeniería de Detalle y en el Planning de proyecto."
          items={disciplines}
          fields={[
            { field: 'name', label: 'Nombre de la disciplina', fullWidth: true },
          ]}
          {...discHandlers}
        />
      </TabPanel>

      {/* Tab 4: Equipment Categories */}
      <TabPanel value={activeTab} index={4}>
        <EquipmentCategories />
      </TabPanel>

      {/* Tab 4: User Category Management */}
      <TabPanel value={activeTab} index={5}>
        <CategoryManager />
      </TabPanel>

      {/* Tab 5: Company */}
      <TabPanel value={activeTab} index={6}>
        <CompanySettings
          onSuccess={(msg) => { setSuccess(msg); setTimeout(() => setSuccess(null), 4000); }}
          onError={(msg) => setError(msg)}
        />
      </TabPanel>

      {/* Tab 6: Document paths */}
      <TabPanel value={activeTab} index={7}>
        <DocumentSettings
          onSuccess={(msg) => { setSuccess(msg); setTimeout(() => setSuccess(null), 4000); }}
          onError={(msg) => setError(msg)}
        />
      </TabPanel>

      {/* Tab 8: Database backups */}
      <TabPanel value={activeTab} index={8}>
        <DatabaseBackupPanel />
      </TabPanel>
    </Box>
  );
}
